import React from "react";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const ScheduledRuns = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="6" 
        title="Scheduled Runs & Quota Management" 
        description="Runs are discrete testing windows that execute within a Program. Each run consumes subscription quota units and progresses through a defined lifecycle."
      />


      <h2 id="run-types" className="text-xl font-bold text-black mt-10 mb-4">6.1 Run Architecture & Managed Engagements</h2>
      <p className="mb-6 text-slate-600 leading-relaxed">Runs are the execution phase of a program. The scheduling experience differs fundamentally based on your program's strategy.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="rounded-2xl border border-blue-100 p-6 bg-blue-50/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg text-lg">💡</div>
            <h3 className="font-bold text-black">One-off Programs</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">For One-off/Pilot programs, the run is <b>automatically managed</b> as a single atomic engagement. You do not need to manually create runs.</p>
          <ul className="text-[11px] space-y-2 list-none p-0">
            <li className="flex items-center gap-2 text-slate-600 font-bold">
              <span className="w-1 h-1 rounded-full bg-blue-400" />
              <span>Bypasses manual scheduling step.</span>
            </li>
            <li className="flex items-center gap-2 text-slate-600">
              <span className="w-1 h-1 rounded-full bg-blue-400" />
              <span>Scope is verified once for the entire lifecycle.</span>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-[#eaeaea] p-6 bg-[#fafafa]/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 text-slate-600 rounded-lg text-lg">🔄</div>
            <h3 className="font-bold text-black font-black">Continuous Programs</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">Requires manual scheduling of <b>Quick Runs</b> or <b>Comprehensive Runs</b> within your subscription quota.</p>
          <p className="text-[11px] text-slate-600 italic">Select the run type that aligns with your specific testing requirements and available unit quota.</p>
        </div>
      </div>

      <h2 id="lifecycle" className="text-xl font-bold text-black mt-12 mb-4">6.2 Run Lifecycle & Operational States</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="p-4 border border-[#eaeaea] rounded-xl bg-white group hover:border-blue-500/20 transition-all">
             <div className="flex items-center gap-2 mb-2">
               <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]" />
               <b className="text-black text-sm">Scheduled</b>
             </div>
             <p className="text-xs text-slate-500 m-0 leading-relaxed">Targets can be freely updated while in this state. The system requires a lead time for researcher assignment.</p>
          </div>
          <div className="p-4 border border-[#eaeaea] rounded-xl bg-white group hover:border-emerald-500/20 transition-all">
             <div className="flex items-center gap-2 mb-2">
               <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
               <b className="text-black text-sm">Active (Testing)</b>
             </div>
             <p className="text-xs text-slate-500 m-0 leading-relaxed">Researchers are hands-on. <b>Pause</b> and <b>Resume</b> actions are available for maintenance windows.</p>
          </div>
          <div className="p-4 border border-[#eaeaea] rounded-xl bg-white group hover:border-amber-500/20 transition-all">
             <div className="flex items-center gap-2 mb-2">
               <span className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]" />
               <b className="text-black text-sm">Paused</b>
             </div>
             <p className="text-xs text-slate-500 m-0 leading-relaxed">Cycle time is suspended. Use during staging outages to preserve your testing window.</p>
          </div>
          <div className="p-4 border border-[#eaeaea] rounded-xl bg-white group hover:border-slate-500/20 transition-all">
             <div className="flex items-center gap-2 mb-2">
               <span className="w-2 h-2 rounded-full bg-slate-400 shadow-[0_0_8px_rgba(148,163,184,0.4)]" />
               <b className="text-black text-sm">Completed</b>
             </div>
             <p className="text-xs text-slate-500 m-0 leading-relaxed">Testing window closed. Results are consolidated into the immutable Pentest Report.</p>
          </div>
      </div>

      <h2 id="quota" className="text-xl font-bold text-black mt-12 mb-4">6.3 Quota Monitoring</h2>
      <p className="text-slate-600 leading-relaxed text-sm">The platform tracks your "Left of X" balance in real-time. Business users can monitor unit consumption, projected depletion dates, and historical run costs via the quota dashboard to ensure alignment with testing objectives.</p>
    </SearchHighlighter>
  );
};

export default ScheduledRuns;
